/** @jsxImportSource @emotion/react */
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { Table } from "../../../components/table-components/Table";
import { TableBody } from "../../../components/table-components/TableBody";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { firstTableCellStyle } from "../../../style-util";
import { KioskConfiguration } from "../models/kiosk-configuration";
import { useDeleteKioskConfiguration } from "../service/kiosk-configuration-api";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { MutateKioskConfigurationDrawer } from "./MutateKioskConfigurationDrawer";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";

interface Props {
  kioskConfigurations: KioskConfiguration[];
}

export const KioskConfigurationsTable = ({ kioskConfigurations }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { option, setOption, removeOption } = useSearchParamsManipulation();

  const {
    mutateAsync: deleteKioskConfiguration,
    isLoading: isDeletingKioskConfiguration,
  } = useDeleteKioskConfiguration();

  return (
    <>
      <Table>
        <thead>
          <TableRowHeader>
            <TableCellHeaderText css={firstTableCellStyle}>
              Name
            </TableCellHeaderText>
            <TableCellHeaderText>Title</TableCellHeaderText>
            <TableCellHeaderText>Version</TableCellHeaderText>
            <th />
          </TableRowHeader>
        </thead>
        <TableBody>
          {kioskConfigurations.map((kioskConfiguration, i) => (
            <TableRow key={i}>
              <TableCellText
                css={firstTableCellStyle}
                onClick={() => {
                  setOption(kioskConfiguration.id);
                }}
              >
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {kioskConfiguration.name || "-"}
                </StyledText>
              </TableCellText>
              <TableCellText
                onClick={() => {
                  setOption(kioskConfiguration.id);
                }}
              >
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {kioskConfiguration.title || "-"}
                </StyledText>
              </TableCellText>
              <TableCellText
                onClick={() => {
                  setOption(kioskConfiguration.id);
                }}
              >
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {kioskConfiguration.version || "-"}
                </StyledText>
              </TableCellText>

              <TableEllipsisDropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <DropdownItemButton
                          onClick={() => {
                            setOption(kioskConfiguration.id);
                          }}
                          type="button"
                          icon={<FontAwesomeIcon icon={solid("pen")} />}
                        >
                          Edit kiosk configuration
                        </DropdownItemButton>
                      ),
                    },
                    {
                      key: "2",
                      label: (
                        <DropdownItemButton
                          onClick={async () => {
                            try {
                              await deleteKioskConfiguration({
                                data: { id: kioskConfiguration.id },
                                organizationKeyName,
                              });
                              toastr.success(
                                "Success",
                                "Kiosk configuration deleted"
                              );
                            } catch (error: any) {
                              // Getting status 500. Most cases it's because of the product that is using this packing item
                              const message = getErrorMessage(error);
                              toastr.error("Error", message);
                            }
                          }}
                          type="button"
                          icon={
                            isDeletingKioskConfiguration ? (
                              <ActionSpinner />
                            ) : (
                              <FontAwesomeIcon icon={solid("trash")} />
                            )
                          }
                          disabled={isDeletingKioskConfiguration}
                        >
                          Delete kiosk configuration
                        </DropdownItemButton>
                      ),
                    },
                  ],
                }}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <MutateKioskConfigurationDrawer
        isOpen={!!option}
        onClose={() => {
          removeOption();
        }}
        kioskConfiguration={kioskConfigurations.find(
          (kioskConfiguration) => kioskConfiguration.id === option
        )}
      />
    </>
  );
};
